// Story 2.2 — Admin portfolios list at /admin/portfolios.
//
// Status + kind filter bar + paginated card list + "+ New portfolio"
// CTA linking to the composer. Detail link per row → Story 2.2's read
// surface; Story 2.3 will add the release-as-drop CTA to the detail
// page.

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Skeleton,
} from "@greenhold/components";

import { EmptyState } from "../components/EmptyState";
import {
  DEFAULT_ADMIN_PORTFOLIO_FILTERS,
  useAdminPortfolios,
  type AdminPortfolioListFilters,
  type AdminPortfolioListRow,
  type PortfolioKind,
  type PortfolioRecruitingStatus,
} from "../features/admin/portfolios/useAdminPortfolios";
import {
  clearLegacyLocalDrafts,
  deleteComposerDraft,
  listComposerDrafts,
  peekLegacyLocalDrafts,
  upsertComposerDraft,
} from "../features/admin/portfolios/composerDrafts";

const STATUS_OPTIONS: { value: PortfolioRecruitingStatus | "any"; label: string }[] = [
  { value: "any", label: "All statuses" },
  { value: "recruiting", label: "Recruiting" },
  { value: "composed", label: "Composed" },
  { value: "retired", label: "Retired" },
];

const KIND_OPTIONS: { value: PortfolioKind | "any"; label: string }[] = [
  { value: "any", label: "All kinds" },
  { value: "regular", label: "Regular" },
  { value: "special", label: "Special" },
  { value: "welcome", label: "Welcome" },
];

export function AdminPortfoliosListPage() {
  const [filters, setFilters] = useState<AdminPortfolioListFilters>(
    DEFAULT_ADMIN_PORTFOLIO_FILTERS,
  );
  const query = useAdminPortfolios(filters);

  // Composer drafts are server-stored (see composerDrafts.ts) so they
  // follow the admin across machines.
  const queryClient = useQueryClient();
  const draftsQuery = useQuery({
    queryKey: ["admin-composer-drafts"],
    queryFn: listComposerDrafts,
    staleTime: 15_000,
    retry: false,
  });
  const drafts = draftsQuery.data ?? [];

  // One-time migration: drafts saved in the localStorage era get
  // uploaded to the server, then the local copies clear — only after
  // every upload succeeded, so a dead backend can't eat them.
  useEffect(() => {
    const legacy = peekLegacyLocalDrafts();
    if (legacy.length === 0) return;
    void (async () => {
      try {
        await Promise.all(legacy.map(upsertComposerDraft));
        clearLegacyLocalDrafts();
        await queryClient.invalidateQueries({
          queryKey: ["admin-composer-drafts"],
        });
        toast.success(
          `${legacy.length} local draft${legacy.length === 1 ? "" : "s"} moved to the server.`,
        );
      } catch {
        // Leave local copies in place; next visit retries.
      }
    })();
  }, [queryClient]);

  async function removeDraft(id: string) {
    try {
      await deleteComposerDraft(id);
      await queryClient.invalidateQueries({
        queryKey: ["admin-composer-drafts"],
      });
    } catch (err) {
      toast.error(`Couldn't delete the draft: ${(err as Error).message}`);
    }
  }

  function setFilter<K extends keyof AdminPortfolioListFilters>(
    key: K,
    value: AdminPortfolioListFilters[K],
  ) {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  }

  function setPage(n: number) {
    setFilters((prev) => ({ ...prev, page: n }));
  }

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
              Portfolios
            </h1>
            <p className="opacity-80">
              Compose new portfolios or drill into an existing one to (Story
              2.3) release it as a drop.
            </p>
          </div>
          <Link to="/admin/portfolios/new">
            <Button variant="primary">+ New portfolio</Button>
          </Link>
        </header>

        {drafts.length > 0 ? (
          <section className="mb-8" aria-label="Composer drafts">
            <h2 className="text-sm font-semibold uppercase tracking-wider opacity-70 mb-3">
              Drafts
            </h2>
            <div className="grid gap-2">
              {drafts.map((d) => (
                <Card key={d.id}>
                  <CardContent className="py-3 flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-medium">
                        {d.title.trim() || "Untitled draft"}
                      </p>
                      <p className="text-xs opacity-60 mt-0.5">
                        Step {d.step} of 5 · {d.selectedIds.length} creators ·{" "}
                        {d.kind} · saved{" "}
                        {d.saved_at
                          ? new Date(d.saved_at).toLocaleString()
                          : "unknown"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/admin/portfolios/new?draft=${d.id}`}>
                        <Button variant="secondary" size="sm">
                          Continue →
                        </Button>
                      </Link>
                      <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => void removeDraft(d.id)}
                        aria-label={`Delete draft ${d.title.trim() || "Untitled draft"}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        ) : null}

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <label className="text-sm">
            <span className="sr-only">Filter by status</span>
            <select
              value={filters.recruiting_status ?? "any"}
              onChange={(e) =>
                setFilter(
                  "recruiting_status",
                  e.target.value as PortfolioRecruitingStatus | "any",
                )
              }
              className="rounded-md border border-brand-forest-200 bg-brand-paper-100 px-3 py-2 text-sm"
              aria-label="Filter by recruiting status"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="sr-only">Filter by kind</span>
            <select
              value={filters.kind ?? "any"}
              onChange={(e) =>
                setFilter("kind", e.target.value as PortfolioKind | "any")
              }
              className="rounded-md border border-brand-forest-200 bg-brand-paper-100 px-3 py-2 text-sm"
              aria-label="Filter by kind"
            >
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {query.isPending ? (
          <div className="grid gap-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        ) : query.isError ? (
          <p className="text-destructive">
            Couldn't load portfolios: {query.error.message}
          </p>
        ) : (query.data?.data.items.length ?? 0) === 0 ? (
          <EmptyState
            aria-label="No portfolios"
            description="No portfolios yet."
            action={
              <Link
                to="/admin/portfolios/new"
                className="underline decoration-dotted"
              >
                Compose your first portfolio →
              </Link>
            }
          />
        ) : (
          <div className="grid gap-3">
            {query.data!.data.items.map((p) => (
              <PortfolioRow key={p.id} portfolio={p} />
            ))}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between gap-3">
          <Button
            variant="secondary"
            size="sm"
            disabled={filters.page <= 1}
            onClick={() => setPage(filters.page - 1)}
          >
            ← Previous
          </Button>
          <p className="text-xs opacity-60 font-mono">Page {filters.page}</p>
          <Button
            variant="secondary"
            size="sm"
            disabled={!query.data?.data.has_next}
            onClick={() => setPage(filters.page + 1)}
          >
            Next →
          </Button>
        </div>
      </div>
    </main>
  );
}

function PortfolioRow({ portfolio }: { portfolio: AdminPortfolioListRow }) {
  return (
    <Link
      to={`/admin/portfolios/${portfolio.id}`}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-700 rounded-lg"
    >
      <Card className="hover:bg-brand-paper-200 transition-colors">
        <CardContent className="py-4 flex items-center justify-between gap-4">
          <div>
            <p className="font-medium">{portfolio.title}</p>
            {portfolio.theme ? (
              <p className="text-sm opacity-70">{portfolio.theme}</p>
            ) : null}
            <p className="text-xs opacity-60 mt-1">
              Composed on{" "}
              {new Date(portfolio.created_at).toLocaleDateString()} ·{" "}
              {portfolio.creator_count} creators
              {portfolio.target_creator_count
                ? ` of ${portfolio.target_creator_count} target`
                : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{portfolio.kind}</Badge>
            <Badge
              variant={
                portfolio.recruiting_status === "composed"
                  ? "success"
                  : portfolio.recruiting_status === "recruiting"
                    ? "warning"
                    : "secondary"
              }
            >
              {portfolio.recruiting_status}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
